import { action, computed, makeObservable, observable } from 'mobx';
import uniq from 'lodash/uniq';

import type { MfaUserStatus, MfaUserMethod } from '@api';

import { Region } from '@components/common/constants';
import { getApiErrorMessage } from '@components/common/util/util';
import { apiAuthCaller } from '@services/apiCaller';
import { IRootStore } from '@stores/index';
import { WorkspaceEnvironment } from '@stores/workspace';
import { ACCESSIBLE_WORKSPACE_STATUSES, WorkspaceStatus } from '@stores/workspace/constants';
import {
  ApiEventUsageStatus,
  API_SOURCE_DEFINITION_CATEGORY,
  BillingPlan,
  EventBySourceDefinitionCategory,
  LockedResource,
  OrgUserStatus,
  getEventUsageStatus,
  getEventsCountsBySourceDefinitionCategory,
  getOrgLockedResources,
  getOrgPlanDetails,
} from '@api';
import { webappClient } from '@lib/bugsnag';

import { isUsageWindowMidCycle, resolveUsageWindowStart } from '@utils/usageWindowUtils';
import { PlanNameType } from './billing';
import { LegacyInviteRole, Member, MemberStore, ServerStatus, UserRoles } from './member';
import { experiment, FeatureFlagKeys } from '@lib/experiment';

type Seed = {
  id: string;
  name: string;
  planName: PlanNameType;
  grafanaURL?: string;
  ownerId: string;
  enforcedMfa?: boolean;
  userRoles: {
    user: {
      id: string;
      email: string;
      name: string;
      status?: OrgUserStatus;
      mfaStatus?: MfaUserStatus;
      mfaMethod?: MfaUserMethod;
      mfaLastUpdated?: string | null;
    };
    roles: UserRoles;
  }[];
  invitedUsers: {
    id: string;
    name: string;
    email: string;
    status: ServerStatus;
    role: LegacyInviteRole;
    userRoles: UserRoles;
    createdAt: string;
  }[];
  workspaceId: string;
  isEligibleForTrial: boolean;
};

export interface Workspace {
  id: string;
  name: string;
  environment: WorkspaceEnvironment;
  status: WorkspaceStatus;
  defaultRegion: Region;
}

export interface IOrganization {
  id: string;
  name: string;
  isLoading: boolean;
  planName?: PlanNameType;
  grafanaURL?: string;
  ownerId: string;
  members: Member[];
  admins: Member[];
  seatHolders: Member[];
  updateEnforcedMfa: (val: boolean) => void;
  updateName: (val: string) => Promise<boolean>;
  seed: (obj: Seed) => void;
  reset: () => void;
  setMemberStatus: (email: string) => void;
  workspaces: Workspace[];
  accessibleWorkspaces: Workspace[];
  workspaceRegions: Region[];
  disabledWorkspaces: Workspace[];
  activeWorkspaces: Workspace[];
  activeOrPausedWorkspaces: Workspace[];
  enforcedMfa?: boolean;
  eventUsageStatus: ApiEventUsageStatus | null;
  eventUsageStatusLoading: boolean;
  loadEventUsageStatus: () => Promise<void>;
  loadOrgPlan: () => Promise<void>;
  totalEventsCount: number;
  totalEventsCountLoading: boolean;
  loadTotalEventsCount: () => Promise<void>;
  eventsUsageCount: number;
  eventsUsageCountLoading: boolean;
  eventsUsageLimit: number;
  usageWindowStartDate: string | null;
  isSelfServe: boolean;
  orgPlan: BillingPlan | null;
  lockedResources: LockedResource[];
  loadLockedResources: () => Promise<void>;
  removeLockedResource: (id: string) => void;
  isEligibleForTrial: boolean;
}

export class OrganizationStore implements IOrganization {
  private rootStore: IRootStore;

  @observable public id = '';

  @observable public name = '';

  @observable public planName?: PlanNameType = undefined;

  @observable public ownerId = '';

  @observable public isLoading = true;

  @observable public isEligibleForTrial = false;

  @observable public grafanaURL: string | undefined = '';

  @observable public members: MemberStore[] = [];

  @observable public enforcedMfa?: boolean = undefined;

  @observable public orgPlan: BillingPlan | null = null;

  @observable public eventUsageStatus: ApiEventUsageStatus | null = null;

  @observable public eventUsageStatusLoading = false;

  @observable public totalEventsCount = 0;

  @observable public totalEventsCountLoading = false;

  @observable public lockedResources: LockedResource[] = [];

  constructor(rootStore: IRootStore) {
    makeObservable(this);
    this.rootStore = rootStore;
  }

  @action.bound
  async loadLockedResources() {
    const { id, isSelfServe, rootStore } = this;

    if (
      !id ||
      !isSelfServe ||
      !experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgLockedResources)
    ) {
      return;
    }

    try {
      const { data } = await getOrgLockedResources(id);
      this.lockedResources = data.data.filter((resource) => resource.locked);
    } catch (e) {
      rootStore.messagesStore.showErrorMessage((e as Error).message);
    }
  }

  @action.bound
  removeLockedResource(id: string) {
    this.lockedResources = this.lockedResources.filter((resource) => resource.resourceId !== id);
  }

  @action.bound
  seed(data: Seed) {
    this.id = data.id;
    this.name = data.name;
    this.planName = data.planName;
    this.grafanaURL = data.grafanaURL;
    this.ownerId = data.ownerId;
    this.enforcedMfa = data.enforcedMfa;
    this.populateMembers(data);
    this.isLoading = false;
    this.eventUsageStatus = null;
    this.isEligibleForTrial = data.isEligibleForTrial;
  }

  private populateMembers({ userRoles, invitedUsers, workspaceId }: Seed) {
    this.members = userRoles.map(
      (userRole) =>
        new MemberStore(
          {
            id: userRole.user.id,
            name: userRole.user.name,
            email: userRole.user.email,
            roles: userRole.roles,
            status: undefined,
            userStatus: userRole.user.status,
            invitedAt: '',
            workspaceId,
            mfaStatus: userRole.user.mfaStatus,
            mfaMethod: userRole.user.mfaMethod,
            mfaLastUpdated: userRole.user.mfaLastUpdated,
          },
          this.rootStore,
        ),
    );

    this.members = this.members.concat(
      invitedUsers.map(
        (user) =>
          new MemberStore(
            {
              id: user.id,
              name: user.name,
              email: user.email,
              status: user.status,
              roles: user.userRoles,
              invitedAt: user.createdAt,
              workspaceId,
            },
            this.rootStore,
          ),
      ),
    );
  }

  @action.bound
  async updateName(name: string) {
    const {
      messagesStore: { showErrorMessage },
    } = this.rootStore;
    try {
      await apiAuthCaller().post(`/updateOrgName`, { name });
      this.name = name;
      return true;
    } catch (error) {
      showErrorMessage(getApiErrorMessage(error, 'Failed to update organization name'));
    }
    return false;
  }

  @action.bound
  setMemberStatus(email: string) {
    const member = this.members.find((user) => user.email === email);
    member!.status = 'pending';
  }

  @action.bound
  async updateEnforcedMfa(enforcedMfa: boolean) {
    const {
      userStore: { updateEnforcedMfa },
      messagesStore,
    } = this.rootStore;
    try {
      await apiAuthCaller().post(`/enforceMfa`, { enforcedMfa });
      this.enforcedMfa = enforcedMfa;
      await updateEnforcedMfa();
      messagesStore.showSuccessMessage('Successfully updated enforced MFA option');
    } catch (error) {
      messagesStore.showErrorMessage('Failed to update enforced MFA option');
    }
  }

  @action.bound
  async loadOrgPlan() {
    if (
      !this.id ||
      !experiment.isFeatureEnabled(FeatureFlagKeys.enablePlgTrial) ||
      !this.rootStore.featuresStore.has('BILLING')
    ) {
      return;
    }
    const response = await getOrgPlanDetails(this.id);
    this.orgPlan = response.data;
  }

  @action.bound
  reset() {
    this.id = '';
    this.name = '';
    this.grafanaURL = '';
    this.ownerId = '';
    this.members = [];
    this.isLoading = true;
    this.orgPlan = null;
    this.totalEventsCount = 0;
    this.totalEventsCountLoading = false;
  }

  @action.bound
  async loadEventUsageStatus() {
    if (!this.isSelfServe) {
      this.eventUsageStatus = null;
      return;
    }

    this.eventUsageStatusLoading = true;

    try {
      const response = await getEventUsageStatus(this.id);
      this.eventUsageStatus = response.data.data;
    } catch (e) {
      this.eventUsageStatus = null;
    } finally {
      this.eventUsageStatusLoading = false;
    }
  }

  @action.bound
  async loadTotalEventsCount() {
    if (!this.isSelfServe) {
      this.totalEventsCount = 0;
      return;
    }

    const { featuresStore, sourcesListStore, billingStore } = this.rootStore;
    const showETLCount = featuresStore.has('ETL') || sourcesListStore.cloudSourcesCount > 0;

    this.totalEventsCountLoading = true;

    try {
      const workspaceIds = this.accessibleWorkspaces.map((w) => w.id).join(',');
      // null → undefined: resolveUsageWindowStart's signature is string | undefined
      const start = resolveUsageWindowStart(billingStore.growthPaidUsageStartAt ?? undefined);

      const responses = await Promise.all(
        this.workspaceRegions.map((region) =>
          getEventsCountsBySourceDefinitionCategory<EventBySourceDefinitionCategory[]>(
            this.id,
            { workspaceIds, start },
            region,
          ),
        ),
      );

      const data = responses.flatMap((response) => response.data);

      const counts = data.reduce(
        (acc, curr) => {
          acc[curr.sourceCategory] += curr.count;
          return acc;
        },
        {
          [API_SOURCE_DEFINITION_CATEGORY.EVENT_STREAM]: 0,
          [API_SOURCE_DEFINITION_CATEGORY.WAREHOUSE_ACTION]: 0,
          [API_SOURCE_DEFINITION_CATEGORY.CLOUD_EXTRACT]: 0,
        },
      );

      const eventStream = counts[API_SOURCE_DEFINITION_CATEGORY.EVENT_STREAM];
      const retl = counts[API_SOURCE_DEFINITION_CATEGORY.WAREHOUSE_ACTION];
      const etl = showETLCount ? counts[API_SOURCE_DEFINITION_CATEGORY.CLOUD_EXTRACT] : 0;

      this.totalEventsCount = eventStream + retl + etl;
    } catch (error) {
      this.totalEventsCount = 0;
      webappClient.notify(error as Error);
    } finally {
      this.totalEventsCountLoading = false;
    }
  }

  @computed get admins() {
    return this.members.filter((member) => member.isOrgAdmin);
  }

  // Matches the backend seat accounting for MAX_TEAM_COUNT: an expired invitation can never
  // become a member and a suspended member is locked out of their seat, so neither holds one.
  @computed get seatHolders() {
    return this.members.filter(
      (member) => member.status !== 'expired' && member.userStatus !== OrgUserStatus.SUSPENDED,
    );
  }

  @computed get isSelfServe() {
    return this.rootStore.featuresStore.has('BILLING');
  }

  @computed get workspaces() {
    const {
      userStore: { workspaces, selectedWorkspaceId },
    } = this.rootStore;
    return workspaces
      .filter((ws) => ws.organizationId === this.id)
      .sort((a, b) => Number(b.id === selectedWorkspaceId) - Number(a.id === selectedWorkspaceId));
  }

  @computed get accessibleWorkspaces() {
    const {
      userStore: { accessibleWorkspaces, selectedWorkspaceId },
      featuresStore,
    } = this.rootStore;
    if (!featuresStore.has('NEW_ROLES_FLOW')) {
      return this.workspaces;
    }

    return accessibleWorkspaces
      .filter((ws) => ws.organizationId === this.id)
      .sort((a, b) => Number(b.id === selectedWorkspaceId) - Number(a.id === selectedWorkspaceId));
  }

  @computed get workspaceRegions() {
    // is this relevant to the data-residency feature?
    return uniq(this.workspaces.map((w) => w.defaultRegion));
  }

  @computed get disabledWorkspaces() {
    return this.workspaces.filter((ws) => ws.status === WorkspaceStatus.DISABLED);
  }

  @computed get activeWorkspaces() {
    return this.workspaces.filter((ws) => ws.status === WorkspaceStatus.ACTIVE);
  }

  @computed get activeOrPausedWorkspaces() {
    return this.workspaces.filter((ws) => ACCESSIBLE_WORKSPACE_STATUSES.includes(ws.status));
  }

  @computed get eventsUsageCount() {
    return this.totalEventsCount;
  }

  @computed get eventsUsageCountLoading() {
    return this.totalEventsCountLoading;
  }

  @computed get eventsUsageLimit() {
    const { billingStore, featuresStore, trialStore } = this.rootStore;
    const { tenantInfo, currentPlan } = billingStore;

    if (this.isSelfServe) {
      const limit = featuresStore.getFeatureConfiguration('EVENT_STREAM_LIMIT') as number;
      const trialLimit = trialStore.isOnTrial
        ? (trialStore.trialTargetPlan?.features?.EVENT_STREAM_LIMIT as number) || limit
        : null;
      const currentPlanLimit = currentPlan?.eventVolume;
      return trialLimit || currentPlanLimit || limit || 0;
    }

    const million = 1_000_000;
    return (tenantInfo?.contractEventVolume || 0) * million;
  }

  @computed get usageWindowStartDate(): string | null {
    // Growth-scoped anchor: non-Growth plans return null from growthPaidUsageStartAt.
    // isUsageWindowMidCycle evaluates monthStart at computation time —
    // stable until the next tenantInfo refresh.
    const anchor = this.rootStore.billingStore.growthPaidUsageStartAt ?? undefined;
    return isUsageWindowMidCycle(anchor) ? anchor : null;
  }
}
